/**
 * Карточка видео — рабочий экран исполнителя.
 *
 * Слева то, что нужно сделать: комментарий, замечания, загрузка.
 * Справа справочное: кто участвует, какие файлы, история.
 */
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Page, PageHeader } from '../components/Layout';
import { FileUploader } from '../components/Uploader';
import { Loading, OverduePill, Person, Pill, Row, toast } from '../components/ui';
import { FILE_KIND, bytes, dateTime, duration, money, timecode, untilDeadline } from '../lib/format';
import type { StoredFile, TeamMember, Video } from '../lib/types';

/**
 * Замена исполнителя в ОДНОМ видео.
 *
 * Отличается от замены в карточке ТЗ тем, что не трогает остальные.
 * Нужно ровно для случая «эти три пусть доделает старый монтажер,
 * а дальше берёт новый»: заменять всё разом там нельзя — человек
 * потеряет деньги за уже начатую работу.
 *
 * Принятое видео переназначить нельзя, это проверяет сервер.
 */
function ChangeOne({
  role,
  videoId,
  currentId,
  people,
  onDone,
}: {
  role: 'worker' | 'editor';
  videoId: string;
  currentId: string | null;
  people: TeamMember[];
  onDone: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [pick, setPick] = useState('');
  const label = role === 'worker' ? 'съёмщика' : 'монтажера';

  const apply = useMutation({
    mutationFn: (userId: string) =>
      api.post(`/api/videos/${videoId}/reassign`,
        role === 'worker' ? { workerId: userId } : { editorId: userId },
      ),
    onSuccess: () => {
      onDone();
      setOpen(false);
      toast(role === 'worker' ? 'Съёмщик заменён' : 'Монтажер заменён');
    },
    onError: (e: Error) => toast(e.message, '⚠'),
  });

  const free = people.filter((p) => p.id !== currentId);

  if (!open) {
    return (
      <button className="btn btn-sm w-full" onClick={() => { setOpen(true); setPick(''); }}>
        Сменить {label} · только это видео
      </button>
    );
  }

  return (
    <div className="space-y-2 bg-bg rounded-lg p-3">
      <div className="label">Кому передать это видео</div>
      <select className="input" value={pick} onChange={(e) => setPick(e.target.value)} autoFocus>
        <option value="">— выбрать —</option>
        {free.map((p) => (
          <option key={p.id} value={p.id}>
            {p.displayName}
            {typeof p.activeVideos === 'number' ? ` · в работе ${p.activeVideos}` : ''}
          </option>
        ))}
      </select>
      <div className="text-[12px] text-muted2 leading-relaxed">
        Остальные видео этого ТЗ останутся на прежних людях. Деньги за это
        видео при приёмке уйдут новому человеку.
      </div>
      <div className="flex gap-2">
        <button className="btn btn-sm flex-1" onClick={() => setOpen(false)}>
          Отмена
        </button>
        <button
          className="btn btn-sm btn-primary flex-1"
          disabled={!pick || apply.isPending}
          onClick={() => apply.mutate(pick)}
        >
          {apply.isPending ? 'Меняю…' : 'Заменить'}
        </button>
      </div>
    </div>
  );
}

export function VideoPage() {
  const { id } = useParams<{ id: string }>();
  const { user, can, hasRole } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [replacingCover, setReplacingCover] = useState(false);
  const [replacingFinal, setReplacingFinal] = useState(false);

  const { data: video, isLoading } = useQuery({
    queryKey: ['video', id],
    queryFn: () => api.get<Video>(`/api/videos/${id}`),
    enabled: Boolean(id),
  });

  // Список людей нужен только тому, кто вправе переставлять исполнителей
  const { data: team } = useQuery({
    queryKey: ['team'],
    queryFn: () => api.get<TeamMember[]>('/api/users'),
    enabled: can('video.reassign'),
  });

  const reloadVideo = () => {
    void qc.invalidateQueries({ queryKey: ['video', id] });
    void qc.invalidateQueries({ queryKey: ['task'] });
  };

  const start = useMutation({
    mutationFn: () => api.post(`/api/videos/${id}/start`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['video', id] });
      void qc.invalidateQueries({ queryKey: ['my-queue'] });
      toast('Взято в работу', '▸');
    },
    onError: (e: Error) => toast(e.message, '⚠'),
  });

  /**
   * Ссылка для плеера. Запрашиваем только когда готовое видео уже есть,
   * иначе сервер справедливо ответит «видео не загружено».
   */
  const { data: playback } = useQuery({
    queryKey: ['playback', id],
    queryFn: () => api.get<{ url: string; ready: boolean }>(`/api/files/playback/${id}`),
    enabled: Boolean(video?.files?.some((f) => f.kind === 'FINAL' && f.uploadStatus === 'COMPLETE')),
    retry: false,
  });

  const download = async (fileId: string) => {
    const res = await api.get<{ url: string }>(`/api/files/${fileId}/download`);
    window.open(res.url, '_blank');
  };

  if (isLoading || !video) {
    return (
      <>
        <PageHeader title="Видео" back="/tasks" />
        <Page>
          <Loading />
        </Page>
      </>
    );
  }

  const isMine = video.worker.id === user?.id || video.editor?.id === user?.id;
  const deadline = untilDeadline(video.currentDeadline ?? video.deadlineShootAt);

  /**
   * Сумма и этап считаются от роли человека В ЭТОМ видео, а не от статуса.
   * Монтажер, открывший видео на стадии съёмки, не должен видеть чужую ставку.
   */
  const isMyShoot = video.worker.id === user?.id;
  const isMyEdit = video.editor?.id === user?.id;
  const isReviewer = can('video.review');
  const myPrice = isReviewer
    ? video.priceShoot + video.priceEdit
    : isMyShoot && isMyEdit
      ? video.priceShoot + video.priceEdit
      : isMyEdit
        ? video.priceEdit
        : video.priceShoot;
  const myStage = isReviewer
    ? ''
    : isMyShoot && isMyEdit
      ? 'съёмка и монтаж'
      : isMyEdit
        ? 'за монтаж'
        : 'за съёмку';

  // Что человек может загрузить прямо сейчас
  const canUploadMaterial =
    isMine &&
    video.mode === 'WITH_EDITOR' &&
    video.worker.id === user?.id &&
    ['NEW', 'SHOOTING', 'AWAITING_EDIT', 'REVISION'].includes(video.status);

  /**
   * Исходник уже сдан и ждёт монтажера — значит это не сдача, а замена.
   * Момент, когда чаще всего замечают, что залили не тот архив.
   */
  const replacingMaterial = canUploadMaterial && video.status === 'AWAITING_EDIT';

  // Принятое и отменённое переназначать нечего — работа закрыта
  const canReassign =
    can('video.reassign') && !['DONE', 'PUBLISHED', 'CANCELLED'].includes(video.status);
  const active = team?.filter((u) => u.status === 'ACTIVE') ?? [];
  const workers = active.filter((u) => u.roles.includes('worker'));
  const editors = active.filter((u) => u.roles.includes('editor'));

  const canUploadFinal =
    isMine &&
    (video.mode === 'SOLO'
      ? ['NEW', 'SHOOTING', 'REVISION'].includes(video.status)
      : video.editor?.id === user?.id && ['EDITING', 'REVISION'].includes(video.status));

  /**
   * Замена уже сданного видео.
   *
   * Нужна, когда в готовом ТЗ оказался не тот файл — перепутали при сдаче
   * или заказчик просит поменять ролики местами. Статус и деньги при этом
   * не трогаются: работа принята и оплачена, меняется только содержимое.
   *
   * Доступно только тому, кто принимает работу.
   */
  const canReplaceFinal =
    can('video.review') && ['DONE', 'PUBLISHED'].includes(video.status);

  /**
   * Пропуск этапа исходников.
   *
   * Обычный съёмщик обязан сдать материал — без него монтажеру нечего
   * делать. Но когда ТЗ ведёт сам администратор и монтирует тоже он,
   * промежуточный архив никому не нужен: он гоняет десять гигабайт
   * туда-обратно ради формальности.
   *
   * Только администратор. У менеджера право приёмки тоже есть, но он
   * такой же исполнитель процесса — его работу проверять надо, иначе
   * контроль качества обходится ролью.
   *
   * Приёмки после такой загрузки тоже не будет — видео примется само,
   * это решает сервер.
   */
  const canSkipMaterial =
    hasRole('admin') &&
    video.mode === 'WITH_EDITOR' &&
    !canUploadFinal &&
    ['NEW', 'SHOOTING', 'REVISION'].includes(video.status);

  const canStart =
    isMine &&
    ((video.status === 'NEW' && video.worker.id === user?.id) ||
      (video.status === 'AWAITING_EDIT' && video.editor?.id === user?.id));

  const unresolvedNotes = video.notes?.filter((n) => !n.resolvedAt) ?? [];

  // Прокси и автокадр — служебные артефакты. Человеку они в списке не нужны:
  // прокси он и так смотрит в плеере, а кадр видит на карточке.
  const visibleFiles = (video.files ?? []).filter(
    (f) => f.uploadStatus === 'COMPLETE' && !FILE_KIND[f.kind]?.internal,
  );
  const cover = visibleFiles.find((f) => f.kind === 'COVER');

  const pin = (scope: 'all' | 'final_only') =>
    void api.post(`/api/files/video/${video.id}/pin`, { pinned: true, scope }).then(() => {
      void qc.invalidateQueries({ queryKey: ['video', id] });
      toast(
        scope === 'all'
          ? 'Закреплено всё, включая исходники'
          : 'Закреплены финал и обложка',
        '📌',
      );
    });

  return (
    <>
      <PageHeader
        title={video.task.title}
        sub={`ТЗ #${video.task.number} · видео ${video.index}`}
        /* Назад — в своё ТЗ, а не в общий список. Раньше отсюда выкидывало
           на «Все ТЗ», и после каждой загрузки обложки приходилось заново
           искать нужное ТЗ и нужное видео */
        back={`/tasks/${video.task.id}`}
        actions={
          can('video.review') && video.status === 'REVIEW' ? (
            <button className="btn btn-primary" onClick={() => navigate(`/review/${video.id}`)}>
              Открыть приёмку
            </button>
          ) : undefined
        }
      />
      <Page>
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-5 items-start">
          <div>
            <div className="flex items-center gap-3 mb-4">
              {video.isOverdue ? <OverduePill /> : <Pill status={video.status} />}
              <span className={`text-[13px] ${deadline.overdue ? 'text-dang' : 'text-muted'}`}>
                {deadline.text}
              </span>
              <div className="flex-1" />
              {/* Исполнителю показываем ЕГО сумму, а не общую по видео:
                  монтажеру видеть 650 ₽ вместо своих 150 — вводить в заблуждение */}
              <div className="text-right">
                <span className="mono font-extrabold text-[17px]">{money(myPrice)}</span>
                {myStage && <div className="text-[11px] text-muted2">{myStage}</div>}
              </div>
            </div>

            {/* Готовое видео можно посмотреть в любой момент, а не только
                при приёмке: чаще всего к нему возвращаются уже после сдачи */}
            {playback && (
              <div className="mb-4">
                <div className="rounded-xl2 overflow-hidden border border-line bg-black">
                  <video src={playback.url} controls className="w-full aspect-video bg-black" />
                </div>
                {!playback.ready && (
                  <div className="text-[11.5px] text-warn mt-2 leading-relaxed">
                    Превью 720p ещё готовится — проигрывается оригинал.
                  </div>
                )}
              </div>
            )}

            <div className="card">
              <div className="sec">Комментарий</div>
              <div className="text-[14.5px] leading-relaxed whitespace-pre-wrap">
                {video.task.comment || <span className="text-muted2">Без комментария</span>}
              </div>
            </div>

            {unresolvedNotes.length > 0 && (
              <>
                <div className="sec">Замечания с приёмки · {unresolvedNotes.length}</div>
                <div className="space-y-2">
                  {unresolvedNotes.map((n) => (
                    <div
                      key={n.id}
                      className="bg-surface2 border border-line border-l-2 border-l-warn rounded-[9px] px-3.5 py-3 text-[13.5px] leading-relaxed"
                    >
                      {n.timecodeSec !== null && (
                        <b className="text-warn mono mr-2">{timecode(n.timecodeSec)}</b>
                      )}
                      {n.text}
                    </div>
                  ))}
                </div>
              </>
            )}

            {canStart && (
              <button
                className="btn btn-primary w-full py-3 mt-5"
                onClick={() => start.mutate()}
                disabled={start.isPending}
              >
                {video.status === 'NEW' ? 'Взять в работу' : 'Взять в монтаж'}
              </button>
            )}

            {canUploadMaterial && (
              <>
                <div className="sec">
                  {replacingMaterial ? 'Замена исходников' : 'Загрузка исходников'}
                </div>
                {replacingMaterial && (
                  <div className="text-[12.5px] text-muted2 leading-relaxed mb-2.5">
                    Залил не тот архив? Загрузи правильный — прежний удалится, а
                    монтажер получит уведомление, что материал заменён, и его срок
                    начнётся заново. Успеть можно, пока он не взял работу в монтаж.
                  </div>
                )}
                <FileUploader
                  videoId={video.id}
                  kind="MATERIAL"
                  onDone={() => void qc.invalidateQueries({ queryKey: ['video', id] })}
                />
              </>
            )}

            {canUploadFinal && (
              <>
                <div className="sec">Загрузка готового видео</div>
                <FileUploader
                  videoId={video.id}
                  kind="FINAL"
                  onDone={() => void qc.invalidateQueries({ queryKey: ['video', id] })}
                />
              </>
            )}

            {canSkipMaterial && (
              <>
                <div className="sec">Сдать готовое сразу</div>
                <div className="text-[12.5px] text-muted2 leading-relaxed mb-2.5">
                  Делаешь это видео сам? Загружай сразу готовый ролик, минуя
                  исходники — гонять архив ради формальности незачем. Видео
                  примется автоматически, приёмки не будет.
                </div>
                <FileUploader
                  videoId={video.id}
                  kind="FINAL"
                  onDone={() => {
                    void qc.invalidateQueries({ queryKey: ['video', id] });
                    toast('Видео сдано и принято');
                  }}
                />
              </>
            )}

            {canReplaceFinal && (
              <>
                <div className="sec">Замена готового видео</div>
                {replacingFinal ? (
                  <>
                    <div className="text-[12.5px] text-muted2 leading-relaxed mb-2.5">
                      Загрузи правильный файл — он встанет на место прежнего.
                      Старое видео вместе с превью удалится, приёмку заново
                      проходить не нужно, деньги не пересчитываются. Ссылка
                      для заказчика останется той же, в ней окажется новый ролик.
                    </div>
                    <FileUploader
                      videoId={video.id}
                      kind="FINAL"
                      onDone={() => {
                        setReplacingFinal(false);
                        void qc.invalidateQueries({ queryKey: ['video', id] });
                        toast('Видео заменено — превью пересоберётся за пару минут');
                      }}
                    />
                    <button
                      className="btn btn-sm w-full mt-2"
                      onClick={() => setReplacingFinal(false)}
                    >
                      Отмена
                    </button>
                  </>
                ) : (
                  <>
                    <div className="text-[12.5px] text-muted2 leading-relaxed mb-2.5">
                      Если в сдаче оказался не тот ролик — можно подменить файл,
                      не отменяя приёмку и не трогая начисления.
                    </div>
                    <button
                      className="btn btn-warn w-full"
                      onClick={() => setReplacingFinal(true)}
                    >
                      Заменить готовое видео
                    </button>
                  </>
                )}
              </>
            )}
          </div>

          <div>
            <div className="sec">Участники</div>
            <div className="card">
              <div className="flex justify-between items-center py-3 border-b border-[#1A1A24] first:pt-0">
                <Person
                  id={video.worker.id}
                  name={video.worker.displayName}
                  sub="съёмка"
                  size={30}
                />
                <b className="mono">{money(video.priceShoot)}</b>
              </div>
              <div className="flex justify-between items-center py-3 last:pb-0">
                {video.editor ? (
                  <Person
                    id={video.editor.id}
                    name={video.editor.displayName}
                    sub="монтаж"
                    size={30}
                  />
                ) : (
                  <span className="text-muted2 text-[13px]">
                    {video.mode === 'SOLO' ? 'Без монтажера' : 'Назначится автоматически'}
                  </span>
                )}
                <b className="mono">{money(video.priceEdit)}</b>
              </div>

              {canReassign && (
                <div className="border-t border-[#1A1A24] pt-3 space-y-2">
                  <ChangeOne
                    role="worker"
                    videoId={video.id}
                    currentId={video.worker.id}
                    people={workers}
                    onDone={reloadVideo}
                  />
                  {video.mode !== 'SOLO' && (
                    <ChangeOne
                      role="editor"
                      videoId={video.id}
                      currentId={video.editor?.id ?? null}
                      people={editors}
                      onDone={reloadVideo}
                    />
                  )}
                </div>
              )}
            </div>

            <div className="sec">Файлы</div>
            <div className="space-y-2">
              {visibleFiles.length === 0 && (
                <div className="text-[12.5px] text-muted2 px-1">Файлов пока нет</div>
              )}
              {visibleFiles.map((f) => (
                <FileRow key={f.id} file={f} onDownload={() => void download(f.id)} />
              ))}
            </div>

            {/* Обложка грузится отдельной картинкой и доступна в любой момент,
                включая уже принятое видео — менеджеру удобнее делать их пачкой */}
            {cover ? (
              <div className="mt-2.5">
                <CoverPreview fileId={cover.id} />
                <button
                  className="btn btn-sm w-full mt-2"
                  onClick={() => setReplacingCover((v) => !v)}
                >
                  {replacingCover ? 'Отмена' : 'Заменить обложку'}
                </button>
                {replacingCover && (
                  <div className="mt-2">
                    <FileUploader
                      videoId={video.id}
                      kind="COVER"
                      compact
                      onDone={() => {
                        setReplacingCover(false);
                        void qc.invalidateQueries({ queryKey: ['video', id] });
                      }}
                    />
                  </div>
                )}
              </div>
            ) : (
              <div className="mt-2.5">
                <FileUploader
                  videoId={video.id}
                  kind="COVER"
                  compact
                  onDone={() => void qc.invalidateQueries({ queryKey: ['video', id] })}
                />
                {video.task.requireCover && (
                  <div className="text-[11.5px] text-warn mt-2 leading-relaxed">
                    В этом ТЗ обложка обязательна — без неё видео нельзя принять.
                  </div>
                )}
              </div>
            )}

            {can('file.pin') && visibleFiles.length > 0 && (
              <div className="mt-3 space-y-2">
                <button className="btn btn-sm w-full" onClick={() => pin('final_only')}>
                  📌 Сохранить финал и обложку
                </button>
                <button className="btn btn-sm w-full" onClick={() => pin('all')}>
                  📌 Сохранить всё, включая исходники
                </button>
                <div className="text-[11px] text-muted2 leading-relaxed px-1">
                  Первое — исходники удалятся по обычному сроку, останется то,
                  что реально пригодится. Второе занимает в разы больше места.
                </div>
              </div>
            )}

            <div className="sec">Сроки</div>
            <div className="card">
              <Row label="Съёмка до">{dateTime(video.deadlineShootAt)}</Row>
              <Row label="Монтаж до">
                {video.editDeadlineAt ? (
                  dateTime(video.editDeadlineAt)
                ) : (
                  <span className="text-muted2 font-normal text-[12.5px]">
                    начнётся после материала
                  </span>
                )}
              </Row>
              <Row label="Доработок">{video.revisionCount}</Row>
            </div>

            <Link to={`/tasks/${video.taskId}`} className="btn btn-sm w-full mt-3 block text-center">
              Все видео этого ТЗ
            </Link>
          </div>
        </div>
      </Page>
    </>
  );
}

/**
 * Обложка прямо в карточке.
 *
 * Смысл простой: обложку нужно ОЦЕНИТЬ, а не сохранить. Заставлять ради
 * этого скачивать файл и открывать его в проводнике — лишние три действия
 * на каждое видео, а их полтораста в месяц.
 */
function CoverPreview({ fileId }: { fileId: string }) {
  const { data } = useQuery({
    queryKey: ['file-view', fileId],
    queryFn: () => api.get<{ url: string }>(`/api/files/${fileId}/view`),
    // Ссылка подписанная и живёт сутки — перезапрашивать её незачем
    staleTime: 12 * 60 * 60 * 1000,
  });

  if (!data) {
    return <div className="aspect-video rounded-[10px] bg-surface2 animate-pulse" />;
  }

  return (
    <a href={data.url} target="_blank" rel="noreferrer" title="Открыть в полном размере">
      <img
        src={data.url}
        alt="Обложка"
        className="w-full aspect-video object-cover rounded-[10px] border border-line
                   hover:border-accent transition-colors"
      />
    </a>
  );
}

/**
 * Строка файла.
 *
 * Главное здесь — крупная подпись, ЧТО это за файл. Раньше было видно
 * только имя вроде «2026-07-30 19-02-51.mp4», по которому невозможно
 * понять, исходники это или готовое видео.
 */
function FileRow({ file, onDownload }: { file: StoredFile; onDownload: () => void }) {
  const meta = FILE_KIND[file.kind] ?? { label: file.kind, icon: '📄' };

  return (
    <div className="flex items-center gap-3 bg-surface2 border border-line rounded-[10px] px-3 py-3">
      <div className="w-9 h-9 shrink-0 rounded-[9px] bg-accent/12 grid place-items-center">
        {meta.icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13.5px] font-bold">{meta.label}</div>
        <div className="text-[11.5px] text-muted2 truncate" title={file.originalName}>
          {file.originalName}
        </div>
        <div className="text-[11px] text-muted2 mt-0.5">
          {bytes(file.sizeBytes)}
          {file.durationSec ? ` · ${duration(file.durationSec)}` : ''}
          {file.width ? ` · ${file.width}×${file.height}` : ''}
          {file.pinned ? (
            <span className="text-warn"> · 📌 сохранён навсегда</span>
          ) : file.expiresAt ? (
            ` · удалится ${dateTime(file.expiresAt)}`
          ) : (
            ''
          )}
        </div>
      </div>
      <button className="btn btn-sm" onClick={onDownload} title="Скачать">
        ⤓
      </button>
    </div>
  );
}
